import React from 'react';
import styled from 'styled-components';

const SEVERITY_COLORS = {
    info: {
        bg: '#e3f2fd',
        border: '#90caf9',
        text: '#0d47a1',
        icon: '#1976d2'
    },
    warning: {
        bg: '#fff8e1',
        border: '#ffecb3',
        text: '#7b5e00',
        icon: '#ffa000'
    },
    error: {
        bg: '#ffebee',
        border: '#ffcdd2',
        text: '#b71c1c',
        icon: '#d32f2f'
    },
    success: {
        bg: '#e8f5e9',
        border: '#c8e6c9',
        text: '#1b5e20',
        icon: '#2e7d32'
    }
};

const AlertRoot = styled.div<{ $bg: string; $border: string; $text: string }>`
    box-sizing: border-box;
    width: 100%;
    display: flex;
    align-items: flex-start;
    padding: 8px 12px;
    margin: 4px 0;
    border-radius: 4px;
    border: 1px solid ${({ $border }) => $border};
    background-color: ${({ $bg }) => $bg};
    color: ${({ $text }) => $text};
    font-size: 14px;
    line-height: 1.4;
`;

const AlertContent = styled.div`
    flex: 1;
    min-width: 0;
`;

const AlertTitle = styled.div`
    font-weight: 600;
    margin-bottom: 2px;
`;

const AlertCloseButton = styled.button`
    border: none;
    background: transparent;
    color: inherit;
    cursor: pointer;
    padding: 0 0 0 8px;
    margin: -2px 0 0 4px;
    font-size: 14px;
    line-height: 1;
    display: inline-flex;
    align-items: center;

    &:hover {
        opacity: 0.75;
    }
`;

type AlertProps = React.HTMLAttributes<HTMLDivElement> & {
    severity?: 'info' | 'warning' | 'error' | 'success';
    title?: React.ReactNode;
    children: React.ReactNode;
    onClose?: () => void;
    className?: string;
};

function Alert({ severity = 'info', title, children, onClose, className, ...rest }: AlertProps) {
    const key = severity as keyof typeof SEVERITY_COLORS;
    const colors = SEVERITY_COLORS[key] || SEVERITY_COLORS.info;

    return (
        <AlertRoot $bg={colors.bg} $border={colors.border} $text={colors.text} className={className} {...rest}>
            <AlertContent>
                {title && <AlertTitle>{title}</AlertTitle>}
                {children}
            </AlertContent>

            {onClose && (
                <AlertCloseButton type="button" aria-label="Close alert" onClick={onClose}>
                    ×
                </AlertCloseButton>
            )}
        </AlertRoot>
    );
}

export default Alert;
